<template>
  <div class="no-plm-access">
    <div class="no-plm-access__card">
      <h1 class="no-plm-access__title">No PLM access</h1>
      <p class="no-plm-access__message">
        You're signed in as <strong>{{ email }}</strong
        >, which doesn't have access to the PLM app here. Log out to sign in with a different account.
      </p>
      <fs-button class="no-plm-access__button" :loading="isLoggingOut" @click="logOut">
        <template #icon><span class="no-plm-access__icon" v-html="signOutIcon" /></template>
        Log out
      </fs-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import FsButton from '@components/buttons/fs-button.vue';
import { intercomShutdown } from '@lib/intercom';
import { useApi } from '@lib/api';
import { useUser } from '@/store/user';
import signOutIcon from '@icons/other/sign-out.svg';

const email = (useUser().current as { userName: string } | null)?.userName;

const api = useApi();
const isLoggingOut = ref(false);

async function logOut() {
  isLoggingOut.value = true;
  // Clear Intercom's session cookies before ours, so the next user on this
  // browser can't see this user's conversations.
  intercomShutdown();
  try {
    await api.account_Logout();
  } finally {
    window.location.assign('/plm/login');
  }
}
</script>

<style lang="scss" scoped>
@import '@styles/variables';

.no-plm-access {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;

  &__card {
    width: 100%;
    max-width: 40rem;
    padding: 3.2rem 2.4rem;
    border-radius: 1rem;
    box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.15);
    text-align: center;
  }

  &__title {
    margin: 0 0 1.6rem;
    color: rgba($c-dark, 0.75);
    font-family: Montserrat, sans-serif;
    font-size: 1.8rem;
    font-weight: 400;
  }

  &__message {
    margin: 0 0 2.4rem;
    color: rgba($c-dark, 0.6);
    font-family: Lato, sans-serif;
    font-size: 1.4rem;
    line-height: 2rem;
  }

  &__button {
    width: 100%;
  }

  &__icon {
    display: flex;
    align-items: center;
    fill: $c-light;

    :deep(svg) {
      width: 1.6rem;
      height: 1.6rem;
    }
  }
}
</style>
